import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { createStockReturn } from '#/api/imsMgmt/receipts/stockReturn/create';
import {
  fetchStockReturnWorkWarehouseSources,
} from '#/api/imsMgmt/receipts/stockReturn/fetchWorkWarehouseSources';
import { updateStockReturn } from '#/api/imsMgmt/receipts/stockReturn/update';
import { Button } from '#/modules/commonComponents/button';
import { ScanCodeDialog, ScanTriggerButton } from '#/modules/commonComponents/scan';
import ColumnTipHeader from '#/modules/commonComponents/table/ColumnTipHeader';
import {
  getStockNoItemId,
  lookupStockNo,
  STOCK_NO_SCAN_LABELS,
} from '#/modules/imsMgmt/receipts/shared/scan';
import {
  getStockReturnApiErrorMessage,
  isStockReturnVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/apiError';
import {
  buildStockReturnWritePayload,
  createReturnDraftLine,
  getReturnLineSourceId,
  mapStockReturnDetailToOperateState,
  maxReturnQtyInUnit,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/formSerialize';
import {
  normalizeReturnWorkWarehouseSources,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/normalizeReturnInventory';
import {
  renderStockReturnAmbiguousRow,
  renderStockReturnScannedRow,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/stockReturnScanRender';
import { stockReturnOperateSchema } from '#/modules/imsMgmt/receipts/stockReturn/schemas/stockReturnSchema';
import { warehouseLabelById } from '#/modules/momMgmt/workOrder/helper/warehouseLabelById';
import { dateToApiDate } from '#/utils/date';
import { clampQtyToAvailable } from '#/utils/quantityBounds';
import {
  stockReturnDetailQueryKey,
  stockReturnListQueryKeyPrefix,
  stockReturnWorkWarehouseSourcesQueryKey,
  stockReturnWorkWarehouseSourcesQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import StockReturnDraftLines from './StockReturnDraftLines';
import StockReturnMaterialList from './StockReturnMaterialList';
import StockReturnOperateHeader from './StockReturnOperateHeader';
import StockReturnPickerDialog from './StockReturnPickerDialog';

const SOURCES_PAGE_SIZE = 200;
const SOURCE_WAREHOUSE_SCAN_TIP = '請先選擇來源倉以載入可用庫存。';
const WRONG_WAREHOUSE_SCAN_MESSAGE = '此庫存不屬於來源倉';
const NOT_IN_SOURCES_SCAN_MESSAGE = '此庫存不在當前可退物料中';

const RETURN_STOCK_NO_SCAN_LABELS = {
  ...STOCK_NO_SCAN_LABELS,
  title: '掃庫存編號加入退料明細',
  description: '掃描或輸入庫存編號，累加至下方清單後確認加入。庫存須屬於來源倉，且物料須在左側可退物料中。待分配不可選。',
};

function compareMtrSortKey(a, b) {
  const keyA = String(a?.mtrNo || a?.mtrId || '');
  const keyB = String(b?.mtrNo || b?.mtrId || '');
  return keyA.localeCompare(keyB, undefined, { numeric: true, sensitivity: 'base' });
}

function matchesReturnMaterialKeyword(material, keyword) {
  const needle = String(keyword ?? '').trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const mtrNo = String(material?.mtrNo ?? '').toLowerCase();
  const mtrName = String(material?.mtrName ?? '').toLowerCase();
  if (mtrNo.includes(needle) || mtrName.includes(needle)) {
    return true;
  }
  return (material?.sources ?? []).some((source) => (
    String(source?.stockNo ?? '').toLowerCase().includes(needle)
  ));
}

function resolveSuccessNavigateTo(successNavigateTo, result) {
  if (typeof successNavigateTo === 'function') {
    return successNavigateTo(result);
  }
  return successNavigateTo;
}

function getOperateValidationMessage(values) {
  const parsed = stockReturnOperateSchema.safeParse(values);
  if (parsed.success) {
    return null;
  }
  return parsed.error.issues[0]?.message || '表單驗證失敗';
}

function isInSourceWarehouse(row, warehouseId) {
  return Boolean(warehouseId && row?.warehouse?.id === warehouseId);
}

function findReturnSource(row, material) {
  return (material?.sources ?? []).find((source) => source.id === row?.id);
}

function isSelectableReturnStock(row, materialsById) {
  const mtrId = row?.mtr?.id;
  const material = mtrId ? materialsById.get(mtrId) : null;
  if (!material) {
    return false;
  }
  const source = findReturnSource(row, material);
  if (source && !source.selectable) {
    return false;
  }
  return true;
}

function formatScanWarehouseLabel(row) {
  const warehouse = row?.warehouse;
  if (!warehouse) {
    return '';
  }
  const warehouseLabel = [warehouse.code, warehouse.name].filter(Boolean).join(' ')
    || warehouse.name
    || '';
  const location = warehouse.location;
  const locationLabel = location
    ? ([location.code, location.name].filter(Boolean).join(' ') || '')
    : '';
  return [warehouseLabel, locationLabel].filter(Boolean).join(' / ');
}

/**
 * 掃描庫存列 → 退料 draft line（欄位對齊選庫 picker）。
 */
function mapScanRowToDraftLine(row, material) {
  const invStockId = row?.id;
  if (!invStockId || !material?.mtrId) {
    return null;
  }

  const source = findReturnSource(row, material);
  if (source && !source.selectable) {
    return null;
  }

  let availableQty;
  let stockNo;
  let fromWarehouseLabel;

  if (source) {
    availableQty = source.availableQty;
    stockNo = source.stockNo;
    fromWarehouseLabel = [source.warehouseLabel, source.locationLabel]
      .filter(Boolean)
      .join(' / ');
  } else {
    availableQty = Number(row.availableQty ?? row.baseQty);
    stockNo = row.stockNo ?? '';
    fromWarehouseLabel = formatScanWarehouseLabel(row);
  }

  return createReturnDraftLine({
    mtrId: material.mtrId,
    mtrNo: material.mtrNo,
    mtrName: material.mtrName,
    invStockId,
    stockNo,
    availableQty,
    fromWarehouseLabel,
  });
}

const StockReturnOperateShell = ({
  warehouseOptions = [],
  isWarehouseLoading = false,
  stockReturnId = null,
  initialDetail = null,
  successNavigateTo = null,
  onDetailRefetch = null,
  submitLabel = null,
  showCancelButton = false,
  cancelNavigateTo = null,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const hydratedDetailIdRef = useRef(null);
  const isEditMode = Boolean(stockReturnId);

  const [returnDate, setReturnDate] = useState(() => dateToApiDate(new Date()) || '');
  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [targetWarehouseId, setTargetWarehouseId] = useState('');
  const [issueReason, setIssueReason] = useState('');
  const [remark, setRemark] = useState('');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [draftLines, setDraftLines] = useState([]);
  const [pickerMaterial, setPickerMaterial] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [stockNoScanOpen, setStockNoScanOpen] = useState(false);
  const [stockNoScanKey, setStockNoScanKey] = useState(0);
  const [focusedMtrId, setFocusedMtrId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  /** 右欄群組收合；僅記錄使用者摺疊，未列者預設展開。 */
  const [collapsedByMtrId, setCollapsedByMtrId] = useState({});

  useEffect(() => {
    if (!initialDetail || !isEditMode) {
      return;
    }
    const detailId = initialDetail.id ?? stockReturnId;
    if (hydratedDetailIdRef.current === detailId) {
      updatedAtRef.current = initialDetail.updatedAt
        ?? initialDetail.record?.updatedAt
        ?? updatedAtRef.current;
      return;
    }

    const state = mapStockReturnDetailToOperateState(initialDetail);
    hydratedDetailIdRef.current = detailId;
    updatedAtRef.current = state.updatedAt;
    setReturnDate(state.returnDate);
    setSourceWarehouseId(state.sourceWarehouseId);
    setTargetWarehouseId(state.targetWarehouseId);
    setIssueReason(state.issueReason);
    setRemark(state.remark);
    setDraftLines(state.draftLines);
    setKeywordDraft('');
    setFocusedMtrId(null);
    setCollapsedByMtrId({});
  }, [initialDetail, isEditMode, stockReturnId]);

  const sourcesListQuery = useMemo(() => ({
    page: 1,
    pageSize: SOURCES_PAGE_SIZE,
    keyword: '',
    sort: [],
  }), []);

  const sourcesQueryKey = useMemo(() => stockReturnWorkWarehouseSourcesQueryKey({
    sourceWarehouseId,
    ...sourcesListQuery,
  }), [sourceWarehouseId, sourcesListQuery]);

  const sourcesQuery = useQuery({
    queryKey: sourcesQueryKey,
    queryFn: () => fetchStockReturnWorkWarehouseSources({
      sourceWarehouseId,
      ...sourcesListQuery,
    }),
    enabled: Boolean(sourceWarehouseId),
  });

  const materials = useMemo(
    () => normalizeReturnWorkWarehouseSources(
      sourcesQuery.data?.data,
      sourcesQuery.data?.warehouse,
    ).materials,
    [sourcesQuery.data],
  );

  const materialsSorted = useMemo(
    () => [...materials].sort(compareMtrSortKey),
    [materials],
  );

  const filteredMaterials = useMemo(
    () => materialsSorted.filter((material) => (
      matchesReturnMaterialKeyword(material, keywordDraft)
    )),
    [materialsSorted, keywordDraft],
  );

  const sortedDraftLines = useMemo(
    () => [...draftLines].sort(compareMtrSortKey),
    [draftLines],
  );

  const pageMeta = sourcesQuery.data?.page;
  const truncatedHint = sourceWarehouseId
    && pageMeta
    && Number(pageMeta.total_page) > 1
    ? `目前僅顯示前 ${SOURCES_PAGE_SIZE} 筆（共 ${pageMeta.total_count} 筆）。`
    : null;

  const operatingByMtrId = useMemo(() => {
    const map = {};
    draftLines.forEach((line) => {
      if (!line?.mtrId) {
        return;
      }
      map[line.mtrId] = (map[line.mtrId] || 0) + (Number(line.qty) || 0);
    });
    return map;
  }, [draftLines]);

  const draftLinesByMtrId = useMemo(() => {
    const map = {};
    draftLines.forEach((line) => {
      if (!line?.mtrId) {
        return;
      }
      if (!map[line.mtrId]) {
        map[line.mtrId] = [];
      }
      map[line.mtrId].push({
        stockNo: line.stockNo,
        qty: line.qty,
      });
    });
    return map;
  }, [draftLines]);

  const pickerInitialSelection = useMemo(() => {
    if (!pickerMaterial?.mtrId) {
      return {};
    }
    const map = {};
    draftLines.forEach((line) => {
      const sourceId = getReturnLineSourceId(line);
      if (line.mtrId === pickerMaterial.mtrId && sourceId) {
        map[sourceId] = Number(line.qty) || 0;
      }
    });
    return map;
  }, [draftLines, pickerMaterial]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (isEditMode) {
        return updateStockReturn(stockReturnId, payload);
      }
      return createStockReturn(payload);
    },
    onSuccess: async (result) => {
      const defaultMessage = isEditMode ? '更新退料單成功' : '建立退料單成功';
      toast.success(result?.message || defaultMessage);
      await queryClient.invalidateQueries({
        queryKey: stockReturnListQueryKeyPrefix(),
      });
      if (isEditMode && stockReturnId) {
        const nextUpdatedAt = result?.data?.updatedAt ?? result?.data?.record?.updatedAt;
        if (nextUpdatedAt) {
          updatedAtRef.current = nextUpdatedAt;
        }
        await queryClient.invalidateQueries({
          queryKey: stockReturnDetailQueryKey(stockReturnId),
        });
      }
      if (sourceWarehouseId) {
        await queryClient.invalidateQueries({
          queryKey: stockReturnWorkWarehouseSourcesQueryKeyPrefix(),
        });
      }

      const nextPath = resolveSuccessNavigateTo(successNavigateTo, result);
      if (nextPath) {
        navigate(nextPath);
      }
    },
    onError: (error) => {
      const fallback = isEditMode ? '更新退料單失敗' : '建立退料單失敗';
      toast.error(getStockReturnApiErrorMessage(error, fallback));
      // 待分配／庫存數量可能已被占用：重讀候選再讓使用者送出。
      queryClient.invalidateQueries({
        queryKey: stockReturnWorkWarehouseSourcesQueryKeyPrefix(),
      });
      if (isEditMode && isStockReturnVersionConflict(error)) {
        hydratedDetailIdRef.current = null;
        onDetailRefetch?.();
      }
    },
  });

  function handleSourceWarehouseChange(nextId) {
    if (isEditMode) {
      return;
    }
    setSourceWarehouseId(nextId ?? '');
    setKeywordDraft('');
    setDraftLines([]);
    setFocusedMtrId(null);
    setCollapsedByMtrId({});
  }

  function handleTargetWarehouseChange(nextId) {
    setTargetWarehouseId(nextId ?? '');
  }

  function handleOpenPicker(material) {
    setPickerMaterial(material);
    setPickerOpen(true);
  }

  function handleOpenStockNoScan() {
    if (!sourceWarehouseId) {
      toast.info(SOURCE_WAREHOUSE_SCAN_TIP);
      return;
    }
    setStockNoScanKey((prev) => prev + 1);
    setStockNoScanOpen(true);
  }

  async function lookupStockNoForReturn(code) {
    const result = await lookupStockNo(code);
    const raw = result.items || [];
    const materialsById = new Map(
      materials.map((material) => [material.mtrId, material]),
    );
    const inWarehouse = raw.filter((row) => isInSourceWarehouse(row, sourceWarehouseId));
    if (raw.length > 0 && inWarehouse.length === 0) {
      throw Object.assign(new Error('wrong-warehouse'), { code: 'WRONG_WAREHOUSE' });
    }
    const items = inWarehouse.filter((row) => isSelectableReturnStock(row, materialsById));
    if (inWarehouse.length > 0 && items.length === 0) {
      throw Object.assign(new Error('not-in-sources'), { code: 'NOT_IN_SOURCES' });
    }
    return { items };
  }

  function handleStockNoScanConfirm(selectedRows) {
    const materialsById = new Map(
      materials.map((material) => [material.mtrId, material]),
    );
    const existingIds = new Set(
      draftLines.map((line) => line.invStockId).filter(Boolean),
    );
    const appended = [];

    (selectedRows || []).forEach((row) => {
      const id = row?.id;
      if (!id) {
        return;
      }
      if (!isInSourceWarehouse(row, sourceWarehouseId)) {
        toast.error(WRONG_WAREHOUSE_SCAN_MESSAGE);
        return;
      }
      if (existingIds.has(id)) {
        toast.error('此庫存已在明細中，已跳過');
        return;
      }
      const mtrId = row?.mtr?.id;
      const material = mtrId ? materialsById.get(mtrId) : null;
      if (!material || !isSelectableReturnStock(row, materialsById)) {
        toast.error(`${NOT_IN_SOURCES_SCAN_MESSAGE}，已跳過`);
        return;
      }
      const line = mapScanRowToDraftLine(row, material);
      if (!line) {
        return;
      }
      appended.push(line);
      existingIds.add(id);
    });

    if (appended.length === 0) {
      return;
    }

    setDraftLines((prev) => [...prev, ...appended]);
    const lastMtrId = appended[appended.length - 1]?.mtrId;
    if (lastMtrId) {
      handleFocusMaterial(lastMtrId);
    }
  }

  function handleFocusMaterial(mtrId) {
    if (!mtrId) {
      return;
    }
    const isVisible = filteredMaterials.some((row) => row.mtrId === mtrId);
    if (!isVisible && keywordDraft) {
      setKeywordDraft('');
    }
    setFocusedMtrId(mtrId);
    setFocusToken((prev) => prev + 1);
    setCollapsedByMtrId((prev) => ({ ...prev, [mtrId]: false }));
  }

  function handleToggleDraftGroup(mtrId) {
    setCollapsedByMtrId((prev) => ({
      ...prev,
      [mtrId]: prev[mtrId] !== true,
    }));
  }

  function handlePickerConfirm(lines) {
    const mtrId = pickerMaterial?.mtrId;
    if (!mtrId) {
      return;
    }
    const nextLines = Array.isArray(lines) ? lines : [];
    setDraftLines((prev) => [
      ...prev.filter((item) => item.mtrId !== mtrId),
      ...nextLines,
    ]);
    if (nextLines.length > 0) {
      handleFocusMaterial(mtrId);
    } else if (focusedMtrId === mtrId) {
      setFocusedMtrId(null);
    }
  }

  function handleRemoveDraftLine(sortedIndex) {
    const lineToRemove = sortedDraftLines[sortedIndex];
    if (!lineToRemove) {
      return;
    }
    setDraftLines((prev) => prev.filter((line) => line !== lineToRemove));
  }

  function handleDraftQtyChange(sortedIndex, nextQty, { finalize = false, maxQty } = {}) {
    const target = sortedDraftLines[sortedIndex];
    if (!target) {
      return;
    }
    const availableBase = Number(target.availableQty) || 0;
    const unitMax = maxQty != null
      ? Number(maxQty)
      : maxReturnQtyInUnit(availableBase, target.unitOptions, target.unitId);
    const clampMax = Math.max(unitMax > 0 ? unitMax : availableBase, 1);
    let qty;
    if (!finalize && (nextQty === '' || nextQty == null)) {
      qty = '';
    } else if (!finalize) {
      const parsed = Number(nextQty);
      qty = Number.isFinite(parsed) ? parsed : target.qty;
    } else if (nextQty === '' || nextQty == null) {
      qty = '';
    } else {
      let next = clampQtyToAvailable(nextQty, clampMax, 0);
      const floored = Math.floor(Number(next));
      next = Number.isFinite(floored) ? floored : '';
      if (next !== '' && unitMax > 0) {
        next = Math.min(next, unitMax);
      }
      qty = next;
    }
    setDraftLines((prev) => prev.map((line) => (
      line === target ? { ...line, qty } : line
    )));
  }

  function handleDraftUnitChange(sortedIndex, nextUnitId) {
    const target = sortedDraftLines[sortedIndex];
    if (!target) {
      return;
    }
    const availableBase = Number(target.availableQty) || 0;
    const max = maxReturnQtyInUnit(availableBase, target.unitOptions, nextUnitId);
    const clamped = clampQtyToAvailable(target.qty, Math.max(max, 0), 0);
    const nextQty = max > 0 ? Math.min(clamped, max) : clamped;
    setDraftLines((prev) => prev.map((line) => (
      line === target
        ? { ...line, unitId: nextUnitId, qty: nextQty }
        : line
    )));
  }

  function handleDraftUnitOptionsChange(sortedIndex, unitOptions) {
    const target = sortedDraftLines[sortedIndex];
    if (!target) {
      return;
    }
    setDraftLines((prev) => prev.map((line) => (
      line === target ? { ...line, unitOptions } : line
    )));
  }

  function handleSubmit() {
    if (saveMutation.isPending) {
      return;
    }

    const validationMessage = getOperateValidationMessage({
      returnDate,
      sourceWarehouseId,
      targetWarehouseId,
      issueReason,
      remark,
      draftLines,
    });
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const normalizedLines = draftLines.map((line) => {
      const availableBase = Number(line.availableQty) || 0;
      const max = maxReturnQtyInUnit(availableBase, line.unitOptions, line.unitId);
      const clampMax = Math.max(max > 0 ? max : 1, 1);
      let qty = clampQtyToAvailable(line.qty, clampMax, 0);
      const floored = Math.floor(Number(qty));
      qty = Number.isFinite(floored) ? floored : line.qty;
      if (max > 0) {
        qty = Math.min(qty, max);
      }
      return {
        ...line,
        qty,
      };
    });

    saveMutation.mutate(buildStockReturnWritePayload({
      returnDate,
      sourceWarehouseId,
      targetWarehouseId,
      issueReason,
      remark,
      draftLines: normalizedLines,
    }, {
      updatedAt: isEditMode ? updatedAtRef.current : null,
    }));
  }

  const canSubmit = draftLines.length > 0
    && Boolean(returnDate)
    && Boolean(sourceWarehouseId)
    && Boolean(targetWarehouseId)
    && sourceWarehouseId !== targetWarehouseId
    && !saveMutation.isPending;

  const actionLabel = submitLabel
    || (saveMutation.isPending
      ? (isEditMode ? '儲存中…' : '送出中…')
      : (isEditMode ? '儲存' : '送出'));

  let emptyHint = '請先選擇來源倉以載入可用庫存。';
  if (sourceWarehouseId && sourcesQuery.isLoading) {
    emptyHint = '載入可用庫存中…';
  } else if (sourceWarehouseId && sourcesQuery.isError) {
    emptyHint = getStockReturnApiErrorMessage(sourcesQuery.error, '載入可用庫存失敗');
  } else if (sourceWarehouseId && materials.length === 0) {
    emptyHint = `『${warehouseLabelById(warehouseOptions, sourceWarehouseId)}』 目前無可用庫存。`;
  } else if (sourceWarehouseId && filteredMaterials.length === 0) {
    emptyHint = '找不到符合關鍵字的庫存來源。';
  }

  return (
    <>
      <div className="space-y-4">
        <StockReturnOperateHeader
          warehouseOptions={warehouseOptions}
          sourceWarehouseId={sourceWarehouseId}
          targetWarehouseId={targetWarehouseId}
          onSourceWarehouseChange={handleSourceWarehouseChange}
          onTargetWarehouseChange={handleTargetWarehouseChange}
          returnDate={returnDate}
          onReturnDateChange={setReturnDate}
          issueReason={issueReason}
          onIssueReasonChange={setIssueReason}
          remark={remark}
          onRemarkChange={setRemark}
          isWarehouseLoading={isWarehouseLoading}
          isSourceWarehouseLocked={isEditMode}
        />

        <div
          className="
            grid grid-cols-1 gap-4
            @4xl/sheet:grid-cols-2
          "
        >
          <section className="form-section min-w-0">
            <h3 className="form-subsection__title my-1!">
              <ColumnTipHeader
                label="可退物料"
                tip="點選列可在右側查看已加入庫存"
              />
            </h3>
            <div className="mt-4 create-edit-panel-scroll overflow-x-auto">
              <StockReturnMaterialList
                materials={filteredMaterials}
                operatingByMtrId={operatingByMtrId}
                draftLinesByMtrId={draftLinesByMtrId}
                focusedMtrId={focusedMtrId}
                focusToken={focusToken}
                onOpenPicker={handleOpenPicker}
                onFocusMaterial={handleFocusMaterial}
                emptyHint={emptyHint}
                keyword={keywordDraft}
                onKeywordChange={setKeywordDraft}
                keywordDisabled={!sourceWarehouseId}
                truncatedHint={truncatedHint}
              />
            </div>
          </section>

          <section className="form-section min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="form-subsection__title mb-0!">
                <ColumnTipHeader
                  label="已加入明細"
                  tip="點標題列會展開／收合；點子項目會對應左側的可退物料"
                />
              </h3>
              <ScanTriggerButton
                labels={RETURN_STOCK_NO_SCAN_LABELS}
                disabled={!sourceWarehouseId}
                onClick={handleOpenStockNoScan}
              />
            </div>
            <div className="mt-3 create-edit-panel-scroll">
              <StockReturnDraftLines
                lines={sortedDraftLines}
                onRemove={handleRemoveDraftLine}
                onFocusMaterial={handleFocusMaterial}
                onQtyChange={handleDraftQtyChange}
                onUnitChange={handleDraftUnitChange}
                onUnitOptionsChange={handleDraftUnitOptionsChange}
                collapsedByMtrId={collapsedByMtrId}
                onToggleGroup={handleToggleDraftGroup}
                focusedMtrId={focusedMtrId}
                focusToken={focusToken}
                reservedNoticeText={truncatedHint}
              />
            </div>
          </section>
        </div>
      </div>

      <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
        {showCancelButton && cancelNavigateTo ? (
          <Button type="button" variant="outline" onClick={() => navigate(cancelNavigateTo)}>
            取消
          </Button>
        ) : null}
        <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
          {actionLabel}
        </Button>
      </div>

      <StockReturnPickerDialog
        key={pickerMaterial?.mtrId ?? 'closed'}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        material={pickerMaterial}
        initialSelection={pickerInitialSelection}
        onConfirm={handlePickerConfirm}
      />

      <ScanCodeDialog
        key={stockNoScanKey}
        open={stockNoScanOpen}
        onOpenChange={setStockNoScanOpen}
        labels={RETURN_STOCK_NO_SCAN_LABELS}
        lookup={lookupStockNoForReturn}
        getItemId={getStockNoItemId}
        renderScannedRow={renderStockReturnScannedRow}
        renderAmbiguousRow={renderStockReturnAmbiguousRow}
        onConfirm={handleStockNoScanConfirm}
        getErrorMessage={(error) => {
          if (error?.code === 'WRONG_WAREHOUSE') {
            return WRONG_WAREHOUSE_SCAN_MESSAGE;
          }
          if (error?.code === 'NOT_IN_SOURCES') {
            return NOT_IN_SOURCES_SCAN_MESSAGE;
          }
          return error?.message || '查詢庫存編號失敗';
        }}
      />
    </>
  );
};

StockReturnOperateShell.propTypes = {
  warehouseOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  isWarehouseLoading: PropTypes.bool,
  stockReturnId: PropTypes.string,
  initialDetail: PropTypes.object,
  successNavigateTo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  onDetailRefetch: PropTypes.func,
  submitLabel: PropTypes.string,
  showCancelButton: PropTypes.bool,
  cancelNavigateTo: PropTypes.string,
};

export default StockReturnOperateShell;
